import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { FacebookIcon, GoogleIcon } from '~/vendor/cxy/icons/glyphs/components/cxy-components/SocialIcons';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';

import { Button } from '../Button';

import styles from './styles/SocialButton.module.scss';

function toSentanceCase(s: string) {
  const result = s.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

/**
 * No `asChild`. It was declared, destructured and dropped, and forwarding it to `Button` — which
 * does support it — turned out to activate a path this component cannot honour: with a `variant`
 * the child is `<SocialButtonContent />`, so Radix's `Slot` merges `className`, `disabled` and
 * `ref` onto a component that accepts none of them, losing every `socialButtonVariants` class and
 * never attaching the ref. Removing it from the contract is the honest answer; supporting it means
 * making the content slot-compatible first.
 */
export interface SocialButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof socialButtonVariants> {
  isLoading?: boolean;
}

export const socialButtonVariants = cva(styles.button, {
  variants: {
    variant: {
      facebook: styles.facebookButton,
      google: styles.googleButton,
    },
  },
});

const SocialButton = forwardRef<HTMLButtonElement, SocialButtonProps>(
  ({ children, className, disabled, isLoading, variant, ...props }, ref) => {
    // A value, not a component declared during render: as a component React saw a new type on every
    // render and remounted the subtree. Rendered twice below, so it is bound once here.
    const buttonContent = variant ? <SocialButtonContent isLoading={isLoading} variant={variant} /> : children;

    return (
      <Button
        className={clsx(socialButtonVariants({ variant }), className)}
        data-cxy-name="social-button"
        data-testid="button-component"
        disabled={disabled}
        isLoading={isLoading}
        loadingText={isLoading ? <>{buttonContent}</> : ''}
        ref={ref}
        {...props}
      >
        {buttonContent}
      </Button>
    );
  },
);

const SocialButtonContent = ({ isLoading, variant }: { isLoading?: boolean; variant: string }) => {
  const icons = {
    facebook: <FacebookIcon />,
    google: <GoogleIcon />,
  };

  const Icon = () => icons[variant as keyof typeof icons];
  const variantName = toSentanceCase(variant);

  return (
    <>
      <Icon />
      <span>{isLoading ? 'Loading' : `Sign in with ${variantName}`} </span>
    </>
  );
};

export { SocialButton };
